import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { OnoffType } from '@/api/enum/common.enum';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { DefaultValues, useForm } from 'react-hook-form';
import { DataGrid } from '../BodyListSummaryPage';
import {
	UnitSeriesSinglePageResDTO,
	UnitSinglePublishDTO,
} from '@/api/DTO/exercise/meditation.interface';
import { useParams } from 'react-router-dom';
import {
	useGetBodySingleStatusListQuery,
	useSetBodySingleStatusMutation,
} from '@/api/exercise/bodyApiSlice';

interface Props {}

export default function SetSingleStatusDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [setBodySingleStatus] = useSetBodySingleStatusMutation();
	const { bodyId } = useParams();

	const onoffList = [
		{
			id: OnoffType.off,
			name: t('information.off'),
		},
		{
			id: OnoffType.on,
			name: t('information.on'),
		},
	];

	const canList = [
		{ id: true, name: t('meditation.can') },
		{ id: false, name: t('meditation.cannot') },
	];
	const yesOrNoList = [
		{ id: true, name: t('meditation.yes') },
		{ id: false, name: t('meditation.no') },
	];
	const exerciseTypeList = [
		{ id: 1, name: t('meditation.audio') },
		{ id: 2, name: t('meditation.video') },
	];

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetBodySingleStatusListQuery(bodyId || '', {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	const defaultValues: DefaultValues<any> = {};

	const methods = useForm<any>({
		defaultValues,
	});

	const onSubmit = async () => {
		const data: UnitSinglePublishDTO[] = Object.entries(
			methods.getValues()
		).map(([key, value]) => ({
			singleId: key,
			published: value == 1 ? true : false,
		}));
		const response = await setBodySingleStatus({
			singlePublishDTOList: data,
			id: bodyId || '',
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateSingleStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'singleId',
			headerName: t('meditation.exerciseTag'),
			flex: 25,
			renderCell: (params) => {
				const arr = (params.row.tags || []).map(
					(item: { tagNameZh: any }) => item.tagNameZh
				);
				return arr.join(',');
			},
		},
		{
			field: 'titleZh',
			headerName: t('meditation.exerciseTitleZh'),
			flex: 25,
		},
		{
			field: 'mediaType',
			headerName: t('meditation.exerciseType'),
			flex: 15,
			renderCell: (params) => (
				<>{showLabel(params.row.mediaType, exerciseTypeList)}</>
			),
		},
		{
			field: 'canCountTime',
			headerName: t('meditation.isNumberTotal'),
			flex: 20,
			renderCell: (params) => (
				<>{showLabel(params.row.canCountTime, canList)}</>
			),
		},
		{
			field: 'canCollectPoints',
			headerName: t('meditation.isGetAward'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.canCollectPoints, canList)}</>
			),
		},
		{
			field: 'hasNew',
			headerName: t('meditation.isNewExercise'),
			flex: 20,
			renderCell: (params) => <>{showLabel(params.row.hasNew, yesOrNoList)}</>,
		},
		{
			field: 'status',
			headerName: t('meditation.exerciseStatus'),
			flex: 15,
			renderCell: (params: any) => (
				<Chip
					color={params.row.status == OnoffType.on ? 'chipPrimary' : 'default'}
					label={showLabel(params.row.status, onoffList)}
				/>
			),
		},
		{
			field: 'changePublished',
			headerName: t('meditation.onoff'),
			flex: 40,
			renderCell: (params: any) => (
				<AppRadioGroup
					control={methods.control}
					name={params.row.singleId}
					row
					options={onoffList}
				/>
			),
		},
	];

	const transformResponse = useCallback(
		(response?: UnitSeriesSinglePageResDTO[]) => {
			if (!response) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
				});

				methods.setValue(
					item.singleId,
					item.published ? OnoffType.on : OnoffType.off
				);
			});

			return newResponse;
		},
		[]
	);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<AssignmentReturnedOutlinedIcon />}
				onClick={() => setOpen(true)}
			>
				{t('meditation.onoff')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xl'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('meditation.onoff')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={3}>
						<Grid item xs display='flex'>
							<LocalDataGrid
								rows={transformResponse(propertySummary?.data) ?? []}
								columns={columns}
								rowCount={0}
								getRowId={(row) => row.singleId}
								pagination={false}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
